<template>
  <!--
    A form field: label, the control (default slot), then an error or hint.
    `inline` puts the label and hint on the left and the control on the
    right, centered on each other, like a drawer row (for short controls:
    numbers, switches, a small choice). Give the control `:id="fieldId"` and
    `:aria-describedby="describedBy"` (exposed via the slot) so the label and
    message reach it.
  -->
  <div
    class="kb-field"
    :class="{ 'kb-field-inline': inline }"
  >
    <div
      v-if="label || $slots.label"
      class="kb-field-head"
    >
      <label
        :id="`${fieldId}-label`"
        :for="fieldId"
      >
        <slot name="label">{{ label }}</slot>
        <span
          v-if="optional"
          class="kb-field-optional"
        > (optional)</span>
      </label>
      <small
        v-if="inline && hint && !error"
        :id="`${fieldId}-hint`"
        class="kb-field-hint"
      >{{ hint }}</small>
    </div>

    <div class="kb-field-control">
      <slot
        :field-id="fieldId"
        :described-by="describedBy"
        :invalid="Boolean(error)"
      />
    </div>

    <small
      v-if="error"
      :id="`${fieldId}-error`"
      class="kb-field-error"
      role="alert"
    >{{ error }}</small>
    <small
      v-else-if="hint && !inline"
      :id="`${fieldId}-hint`"
      class="kb-field-hint"
    >{{ hint }}</small>
    <slot name="after" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  /** The control's id; the label points at it */
  fieldId: string
  label?: string
  hint?: string
  /** Shown under the control instead of the hint */
  error?: string
  optional?: boolean
  inline?: boolean
}>()

const describedBy = computed(() => {
  if (props.error) return `${props.fieldId}-error`
  return props.hint ? `${props.fieldId}-hint` : undefined
})
</script>
